<template>
  <div class="json-formatter">
    <div class="input-section">
      <el-card class="input-card">
        <template #header>
          <div class="card-header">
            <div class="header-left">
              <h3 class="card-title">JSON Input</h3>
              <div class="input-stats" v-if="jsonInput">
                <span class="stat-item">
                  <span class="stat-icon">📝</span>
                  {{ jsonInput.length }} chars
                </span>
                <span class="stat-item">
                  <span class="stat-icon">📄</span>
                  {{ lineCount }} lines
                </span>
              </div>
            </div>
            <div class="header-right">
              <el-button
                size="small"
                type="default"
                @click="unescapeInput"
                :disabled="!jsonInput"
                class="action-btn"
              >
                <span class="btn-icon">🔓</span>
                Unescape
              </el-button>
              <el-button
                size="small"
                type="default"
                @click="clearInput"
                :disabled="!jsonInput"
                class="action-btn"
              >
                <span class="btn-icon">🗑️</span>
                Clear
              </el-button>
            </div>
          </div>
        </template>
        <div class="input-content">
          <el-input
            v-model="jsonInput"
            type="textarea"
            placeholder="Paste or type your JSON here..."
            @input="handleInput"
            class="json-textarea"
            resize="none"
            :autosize="false"
          />
          <transition name="fade">
            <el-alert
              v-if="jsonError"
              :title="jsonError"
              type="error"
              show-icon
              :closable="false"
              class="error-alert"
            />
          </transition>
        </div>
      </el-card>
    </div>
    <div class="output-section">
      <Output :jsonData="jsonOutput" />
    </div>
  </div>
</template>

<script>
import { ElAlert, ElCard, ElInput, ElButton } from 'element-plus';
import { debounce } from 'lodash-es';
import Output from './JsonOutput.vue';

export default {
  components: { Output, ElInput, ElCard, ElAlert, ElButton },
  data() {
    return {
      jsonInput: '',
      jsonOutput: '',
      jsonError: ''
    };
  },
  computed: {
    lineCount() {
      return this.jsonInput ? this.jsonInput.split('\n').length : 0;
    }
  },
  created() {
    this.debouncedFormatJson = debounce(this.formatJson, 300);
  },
  methods: {
    handleInput() {
      this.debouncedFormatJson();
    },
    formatJson() {
      if (!this.jsonInput.trim()) {
        this.jsonOutput = '';
        this.jsonError = '';
        return;
      }
      try {
        const parsedJson = JSON.parse(this.jsonInput);
        this.jsonOutput = JSON.stringify(parsedJson, null, 2);
        this.jsonError = '';
      } catch (e) {
        this.jsonError = `Invalid JSON: ${e.message}`;
        this.jsonOutput = '';
      }
    },
    unescapeInput() {
      try {
        const parsed = JSON.parse(this.jsonInput);
        this.jsonInput = typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2);
        this.formatJson();
      } catch {
        this.jsonInput = this.jsonInput
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\')
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\r/g, '\r');
        this.formatJson();
      }
    },
    clearInput() {
      this.jsonInput = '';
      this.jsonOutput = '';
      this.jsonError = '';
    }
  }
};
</script>

<style scoped>
.json-formatter {
  display: flex;
  gap: var(--spacing-lg);
  min-height: calc(100vh - 140px);
}

.input-section,
.output-section {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.input-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  background: var(--bg-card);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-lg);
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-card);
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-shrink: 0;
}

.card-title {
  margin: 0;
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text-primary);
  font-family: var(--font-heading);
}

.input-stats {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  color: var(--text-secondary);
  transition: all var(--transition-fast);
}

.stat-item:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.stat-icon {
  font-size: 12px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  font-weight: 500;
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 14px;
}

.input-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.json-textarea {
  flex: 1;
}

.error-alert {
  margin-top: var(--spacing-sm);
  border-radius: var(--radius-md);
  border: none;
  animation: slideDown var(--transition-base);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--transition-base);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:deep(.input-card .el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-md);
}

:deep(.json-textarea .el-textarea__inner) {
  height: 100% !important;
  min-height: 400px;
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  line-height: 1.6;
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  background: var(--bg-card);
  color: var(--text-primary);
  transition: all var(--transition-fast);
}

:deep(.json-textarea .el-textarea__inner:hover) {
  border-color: var(--text-tertiary);
  box-shadow: var(--shadow-sm);
}

:deep(.json-textarea .el-textarea__inner:focus) {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

:deep(.json-textarea .el-textarea__inner::placeholder) {
  color: var(--text-disabled);
}

:deep(.input-card .el-card__header) {
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-card);
}

@media (max-width: 1024px) {
  .json-formatter {
    flex-direction: column;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-right {
    justify-content: flex-end;
  }
}
</style>
